import { Model } from "./model";
import { Time } from "./time";
import { SavingsPaymentsModel } from "./savings_payments";
import { SavingsPaymentAccountModel } from "./savings_payment_account";
import { HasSocketEvent } from "@models/interfaces";
import { SocketEvent } from "@helpers/types";

export declare const enum SavingPlanStatus {
	Active = "active",
	Paused = "paused",
	Canceled = "canceled",
	Pending = "pending"
}

export class SavingsPlan extends Model implements HasSocketEvent {
	// Received from API
	private fiat_wallet_id: string = "";
	private fiat_amount: string = "";
	private cryptocoin_id: string = "";
	private wallet_id: string = "";
	private state: string = "";
	private frequency: string = "";
	private frequency_name: string = "";
	private day: string = "";
	private time_zone: string = "";
	private time: string = "";
	private next_at: Time = new Time();
	private next_trade_at: Time = new Time();
	private created_at: Time = new Time();
	private canceled_at: Time = new Time();
	private savings_payments: SavingsPaymentsModel[] = [new SavingsPaymentsModel()];
	private savings_payment_account: SavingsPaymentAccountModel = new SavingsPaymentAccountModel();
	private is_confirmed: boolean = false;
	private payed_initial: boolean = false;
	private maintenance_enabled: boolean = false;
	private total_spent: string = "";
	private finished_buys: string = "";
	private bfc_used: boolean = false;

	public eventData: SocketEvent = "";

	// Not part of API - used as a flag for the open/close state of the more '...' button
	public isMoreOpen: boolean = false;

	public getFiatWalletId(): string {
		return this.fiat_wallet_id;
	}

	public getAssetWalletId(): string {
		return this.wallet_id;
	}

	public getFiatAmount(): string {
		return this.fiat_amount;
	}

	public getCurrencyId(): string {
		return this.cryptocoin_id;
	}

	public getFrequency(): string {
		return this.frequency;
	}

	public getFrequencyName(): string {
		return this.frequency_name;
	}

	public set bestOptIn(b: boolean) {
		this.bfc_used = b;
	}

	public get bestOptIn(): boolean {
		return this.bfc_used;
	}

	public getDay(): string {
		return this.day;
	}

	public getTimeZone(): string {
		return this.time_zone;
	}

	public getStatus(): string {
		return this.state;
	}

	public setStateActive(): void {
		this.state = SavingPlanStatus.Active;
	}

	public setStatePaused(): void {
		this.state = SavingPlanStatus.Paused;
	}

	public setStateCanceled(): void {
		this.state = SavingPlanStatus.Canceled;
	}

	public setState(s: string): void {
		this.state = s;
	}

	public getTime(): string {
		return this.time;
	}

	public getNextAt(): Time {
		// Useage of this field will be changed in about a month, then it will be used for an updated design
		return this.next_at;
	}

	public getNextTradeAt(): Time {
		return this.next_trade_at;
	}

	public getCreatedAt(): Time {
		return this.created_at;
	}

	public getCanceledAt(): Time {
		return this.canceled_at;
	}

	public getSavingsPayments(): SavingsPaymentsModel[] {
		return this.savings_payments;
	}

	public getSavingPaymentAccount(): SavingsPaymentAccountModel {
		return this.savings_payment_account;
	}

	public isConfirmed(): boolean {
		return this.is_confirmed;
	}

	public payedInitial(): boolean {
		return this.payed_initial;
	}

	public isMaintenanceEnabled(): boolean {
		return this.maintenance_enabled;
	}

	public getTotalSpent(): number {
		return +this.total_spent;
	}

	public setTotalSpent(n: number): void {
		this.total_spent = "" + n;
	}

	public getFinishedBuys(): number {
		return +this.finished_buys;
	}

	public setFinishedBuys(n: number): void {
		this.finished_buys = "" + n;
	}

	public isActive(): boolean {
		return this.getStatus() === SavingPlanStatus.Active;
	}

	public isPaused(): boolean {
		return this.getStatus() === SavingPlanStatus.Paused;
	}

	public isCanceled(): boolean {
		return this.getStatus() === SavingPlanStatus.Canceled;
	}

	public isPending(): boolean {
		return this.getStatus() === SavingPlanStatus.Pending;
	}

	public getEvent(): string {
		return this.eventData;
	}
}
